/**
 * Memberships config hook — reads server-published rules for the memberships
 * module so UI validation thresholds stay in sync with backend.
 *
 * Endpoint: GET /v1/config/memberships
 */

import { useQuery } from '@tanstack/react-query';
import { api } from '@core/api/client';

export interface MembershipsRules {
  pause_reason_min_length: number;
  cancel_reason_min_length: number;
  pause_max_duration_days: number;
  max_skips_ahead: number;
  default_recurrence_unit: string;
  default_recurrence_interval: number;
  flags: {
    memberships_enabled: boolean;
    memberships_allow_pause: boolean;
    memberships_allow_admin_skip: boolean;
    [k: string]: boolean;
  };
}

/** Conservative fallback used while the request is in-flight or if it fails. */
export const MEMBERSHIPS_RULES_FALLBACK: MembershipsRules = {
  pause_reason_min_length: 10,
  cancel_reason_min_length: 10,
  pause_max_duration_days: 365,
  max_skips_ahead: 6,
  default_recurrence_unit: 'month',
  default_recurrence_interval: 1,
  flags: {
    memberships_enabled: true,
    memberships_allow_pause: true,
    memberships_allow_admin_skip: true,
  },
};

export function useMembershipsConfig() {
  const q = useQuery({
    queryKey: ['config', 'memberships'] as const,
    queryFn: () => api.get<MembershipsRules>('/v1/config/memberships'),
    staleTime: 5 * 60 * 1000,
  });
  return { rules: q.data ?? MEMBERSHIPS_RULES_FALLBACK, isLoading: q.isLoading };
}
